import { useState } from "react";
import { format, subDays } from "date-fns";

import Layout from "@/components/layout/Layout";
import { CashFlowStatementHeader } from "@/components/finance/reports/cash-flow-details-new/Header";
import { StatementTable } from "@/components/finance/reports/cash-flow-details-new/StatementTable";
import { SummaryCards } from "@/components/finance/reports/cash-flow-details-new/SummaryCards";
import { TopExpenses } from "@/components/finance/reports/cash-flow-details-new/TopExpenses";

import { MoMMovers } from "@/components/finance/reports/cash-flow-details-new/MoMMovers";
import { ForecastPnl } from "@/components/finance/reports/cash-flow-details-new/ForecastPnl";
import { ExpenseForecast } from "@/components/finance/reports/cash-flow-details-new/ExpenseForecast";
import { BankRealityStrip } from "@/components/finance/reports/cash-flow-details-new/BankRealityStrip";

import {
  useCashFlowStatementDetails,
  type ViewPeriod,
  type DisplayCurrency,
} from "@/hooks/finance/cash-flow/useCashFlow";

export default function CashFlowDetailsNewPage() {
  const today = format(new Date(), "yyyy-MM-dd");
  const thirtyDaysAgo = format(subDays(new Date(), 30), "yyyy-MM-dd");

  const [dateFrom, setDateFrom] = useState(thirtyDaysAgo);
  const [dateTo, setDateTo] = useState(today);
  const [viewPeriod, setViewPeriod] = useState<ViewPeriod>("daily");
  const [displayCurrency, setDisplayCurrency] =
    useState<DisplayCurrency>("ETB");

  const {
  rows,
  periodLabels,
  periodKeys,
  isLoading,
  openingMetaByPeriod,
  closingMetaByPeriod,
} = useCashFlowStatementDetails(
    dateFrom,
    dateTo,
    viewPeriod,
    displayCurrency
  );

  return (
    <Layout>
      <div className="space-y-6 pb-12 animate-in fade-in duration-700">
        <CashFlowStatementHeader
          dateFrom={dateFrom}
          dateTo={dateTo}
          viewPeriod={viewPeriod}
          displayCurrency={displayCurrency}
          onDateFromChange={setDateFrom}
          onDateToChange={setDateTo}
          onViewPeriodChange={setViewPeriod}
          onDisplayCurrencyChange={setDisplayCurrency}
          title="Cash Flow Details"
          badgeLabel="Deep Dive"
          subtitle="Granular liquidity breakdown & drill-down analytics"
        />

        <SummaryCards
          rows={rows}
          periodKeys={periodKeys}
          displayCurrency={displayCurrency}
          isLoading={isLoading}
          dateFrom={dateFrom}
          dateTo={dateTo}
        />

        <StatementTable
          rows={rows}
          periodLabels={periodLabels}
          periodKeys={periodKeys}
          isLoading={isLoading}
          displayCurrency={displayCurrency}
          openingMetaByPeriod={openingMetaByPeriod}
          closingMetaByPeriod={closingMetaByPeriod}
          dateFrom={dateFrom}
          dateTo={dateTo}
        />

        

        <TopExpenses
          rows={rows}
          periodKeys={periodKeys}
          displayCurrency={displayCurrency}
          isLoading={isLoading}
        />

        <MoMMovers
          rows={rows}
          periodKeys={periodKeys}
          periodLabels={periodLabels}
          displayCurrency={displayCurrency}
          isLoading={isLoading}
          viewPeriod={viewPeriod}
        />

        <ForecastPnl displayCurrency={displayCurrency} />

        <ExpenseForecast displayCurrency={displayCurrency} />

        <BankRealityStrip
          rows={rows}
          periodKeys={periodKeys}
          periodLabels={periodLabels}
          dateFrom={dateFrom}
          dateTo={dateTo}
          viewPeriod={viewPeriod}
          displayCurrency={displayCurrency}
          isLoading={isLoading}
        />
      </div>
    </Layout>
  );
}